import { useCallback, useMemo } from 'react';

import { wait } from '@change-corgi/core/async';
import { useUtilityContext } from '@change-corgi/core-react/utilityContext';

import type { PsfPromoteOrSharePageData } from '#src/app/pages/psf/promoteOrShare/pageContext';
import { usePsfPromoteOrSharePageData } from '#src/app/pages/psf/promoteOrShare/pageContext';
import { treatExperiment } from '#src/app/shared/api/experiments';
import { useTrackExperimentExposure } from '#src/app/shared/hooks/tracking';

import type { FacebookPostSheetTreatOutcome } from './firedFacebookPostSheet';
import { facebookPostSheetTreats, firedFacebookPostSheetExposures } from './firedFacebookPostSheet';

/**
 * How long a Facebook tap waits for the treat before the share goes ahead (plan KTD10). The production
 * composer opens inside the tap, before this wait (`shareAtTap`), so the cap bounds how long the share
 * events are held and how late the `variant_1` sheet opens. It covers the treat and the `$exposure`
 * send together.
 */
const FACEBOOK_POST_SHEET_TREAT_BUDGET_MS = 800;

type FacebookPostSheetVariation =
	PsfPromoteOrSharePageData['psfPromoteOrShareFacebookPostSheetExperiment']['variation'];

/** `feed_sheet` opens the feed post sheet. `destination_choice` asks feed or group first (CHANGE-99698). */
export type FacebookPostSheetArm = 'feed_sheet' | 'destination_choice';

// Keyed on the config's own variations union, so a renamed arm fails to compile instead of silently
// rendering production. `control` and `fake_control` are absent on purpose: both render production.
const ARM_SHEETS: Readonly<Partial<Record<FacebookPostSheetVariation, FacebookPostSheetArm>>> = {
	variant_1: 'feed_sheet',
	variant_2: 'destination_choice',
};

async function budgetElapsed(): Promise<'timed_out'> {
	await wait(FACEBOOK_POST_SHEET_TREAT_BUDGET_MS);
	return 'timed_out';
}

export type UseTreatFacebookPostSheetExperimentResult = {
	/** The data-side eligibility plus the experiment-side clauses. The one input to both the treat and the arm. */
	isEligible: boolean;
	/** The sheet the tap opens, or `null` for production behavior. Derived from `isEligible` (plan KTD2). */
	sheetArm: FacebookPostSheetArm | null;
	/** Whether a tap treats, and so waits for the treat: `isEligible`, and not a QA/dev `?exo` override. */
	treatsAtTap: boolean;
	/**
	 * Treats at the Facebook tap, in every arm, and waits for the treat for at most the treat budget.
	 * Fires `$exposure` once per session, if the treat registered within the budget, and waits for the
	 * send within the same budget, so the caller's share events come after it. Skips the treat for a
	 * QA/dev `?exo` override. Resolves to the outcome, for the probe.
	 */
	treatAtTap: () => Promise<FacebookPostSheetTreatOutcome>;
};

/**
 * psf_promote_or_share_facebook_post_sheet_experiment (CHANGE-99697): treat and `$exposure` at the
 * Facebook tap on the PSF share slide-up, so the treated population equals the exposed population
 * equals eligible supporters who tap Facebook, apart from taps whose treat misses the budget.
 *
 * `isDataEligible` comes from `useFacebookPostSheetEligibility`: the kill switch, a ready caption and
 * one or more groups, and an English locale. Those inputs are arm-independent, so every arm draws from
 * one population (plan R5).
 */
export function useTreatFacebookPostSheetExperiment(
	isDataEligible: boolean,
): UseTreatFacebookPostSheetExperimentResult {
	const {
		data: { psfPromoteOrShareFacebookPostSheetExperiment: experiment },
	} = usePsfPromoteOrSharePageData();
	const utilityContext = useUtilityContext();

	// `treatExperiment` bypasses the config's `enabledIf`, so the flag is read again here. `!fallback`
	// keeps treated == exposed: `useTrackExperimentExposure` refuses to expose a fallback resolution, so
	// treating one would enroll a session that can never expose. It already covers the sentinel name,
	// which is kept as belt and braces.
	const isEligible =
		isDataEligible &&
		experiment.config.enabled &&
		experiment.experimentName !== '__invalid_experiment__' &&
		!experiment.fallback;

	// `treatExperiment` registers the treatment server-side, but the resolved object stays
	// `treated: false`, and `useTrackExperimentExposure` does nothing while `!treated`. So the exposure
	// object is armed with `treated: true` here. Safe only because the one call site below fires it on
	// `treatExperiment`'s own `true` return. Do not call `trackExposure` from anywhere else.
	// An override forces the arm the page shows, but `treatExperiment` would register the session's real
	// assignment, and `useTrackExperimentExposure` never exposes an override. So an override session sees
	// its forced arm (`sheetArm` still reads `isEligible`) and is never treated.
	const treatsAtTap = isEligible && !experiment.override;

	const experimentForExposure = useMemo(() => ({ ...experiment, treated: true }), [experiment]);
	const trackExposure = useTrackExperimentExposure(experimentForExposure);

	const treatAtTap = useCallback(async (): Promise<FacebookPostSheetTreatOutcome> => {
		if (!treatsAtTap) return 'skipped';
		const { experimentName } = experiment;

		let treat = facebookPostSheetTreats.get(experimentName);
		if (!treat) {
			treat = (async () => {
				// `treatExperiment` resolves `false`, rather than throwing, when the treatment did not land
				// server-side (network failure, bot session). A throw is treated the same way.
				try {
					return (await treatExperiment(utilityContext, experimentName)) ? 'registered' : 'not_registered';
				} catch {
					return 'not_registered';
				}
			})();
			facebookPostSheetTreats.set(experimentName, treat);
		}

		// One budget for the treat and the exposure send. Its timer is not cancelled when they win: it
		// resolves later, unobserved.
		const budget = budgetElapsed();
		const outcome = await Promise.race([treat, budget]);

		if (outcome === 'registered' && !firedFacebookPostSheetExposures.has(experimentName)) {
			// Claimed synchronously, so two mounts that settle in the same tick cannot both fire. Events carry
			// no client timestamp, so the send is awaited: the caller's share events must not reach the
			// server before `$exposure`. The wait shares the treat's budget, so the tap still waits at most
			// the budget before `window.open`. A failed send releases the claim, so another `ShareSlideUp`
			// mount can retry. The same mount cannot: `useTrackExperimentExposure` sends at most once per
			// mount.
			firedFacebookPostSheetExposures.add(experimentName);
			const sending = (async () => {
				let sent = false;
				try {
					sent = await trackExposure();
				} catch {
					// Same as a `false` return: release the claim below.
				}
				if (!sent) firedFacebookPostSheetExposures.delete(experimentName);
			})();
			await Promise.race([sending, budget]);
		}

		return outcome;
	}, [treatsAtTap, experiment, utilityContext, trackExposure]);

	const sheetArm = (isEligible && ARM_SHEETS[experiment.variation]) || null;

	return { isEligible, sheetArm, treatsAtTap, treatAtTap };
}
